import { IconMapPin, IconPalette, IconSparkles, IconTool, type TablerIconsProps } from '@tabler/icons-react';
import type { ComponentType } from 'react';
import { useTheme } from '@contexts/ThemeContext';
import { resolvePatchLogCopy } from './i18n';
import {
  pickLocalized,
  type PatchLogCategory,
  type PatchLogEntryPublic,
  type PatchLogImagePublic,
  type PatchLogLocale,
} from './types';

/**
 * Category is always shown as color + icon + text — never color alone. Accent
 * hexes are picked per light/dark so the small uppercase label stays legible on
 * the translucent receipt surface in either palette.
 */
const CATEGORY_META: Record<PatchLogCategory, { icon: ComponentType<TablerIconsProps>; light: string; dark: string }> =
  {
    fitur_baru: { icon: IconSparkles, light: '#0F766E', dark: '#5EEAD4' },
    perbaikan: { icon: IconTool, light: '#1D4ED8', dark: '#93C5FD' },
    tampilan_berubah: { icon: IconPalette, light: '#6D28D9', dark: '#C4B5FD' },
  };

const byPosition = (a: PatchLogImagePublic, b: PatchLogImagePublic) => a.position - b.position;

interface PatchLogEntryCardProps {
  entry: PatchLogEntryPublic;
  locale: PatchLogLocale;
  /** First entry of the modal: its images are above the fold, so load them eagerly. */
  eager?: boolean;
  /**
   * Opt-in full-screen zoom (history page only). Passing it turns every figure
   * into a real button that hands the image back; leaving it out renders the
   * plain <img> the blocking modal has always shown — that modal must never
   * gain a way to open an overlay above itself.
   */
  onZoomImage?: (image: PatchLogImagePublic) => void;
  /**
   * Wide layout: the card was drawn for a 410px modal column, and the history
   * page gives it far more room. Only widens the image frames (>= 720px on
   * desktop, full width on mobile) — the modal keeps its own sizing.
   */
  wide?: boolean;
}

/**
 * One entry of the receipt. No box of its own — entries are separated by the
 * dashed rule in patch-logs.css, so the modal is a single surface rather than
 * cards inside a card.
 */
export const PatchLogEntryCard = ({
  entry,
  locale,
  eager = false,
  onZoomImage,
  wide = false,
}: PatchLogEntryCardProps) => {
  const { isDark } = useTheme();
  const meta = CATEGORY_META[entry.category] ?? CATEGORY_META.perbaikan;
  const accent = isDark ? meta.dark : meta.light;
  const CategoryIcon = meta.icon;

  const title = pickLocalized(entry.title, locale, '');
  // Optional and often absent; an empty string in every locale means the change
  // is cross-cutting, so the label is dropped rather than rendered blank.
  const area = pickLocalized(entry.area, locale, '');
  const points = pickLocalized(entry.points, locale, []);

  const images = [...(entry.images ?? [])].sort(byPosition);
  const singles = images.filter((image) => image.type === 'single');
  const befores = images.filter((image) => image.type === 'before');
  const afters = images.filter((image) => image.type === 'after');
  // Pairs by order; the publish gate guarantees both sides exist, but a
  // dangling side is still rendered rather than dropped.
  const pairCount = Math.max(befores.length, afters.length);
  const pairs = Array.from({ length: pairCount }, (_, index) => ({
    before: befores[index],
    after: afters[index],
  }));

  const frameClassName = wide ? 'jarvis-patch-log__frame jarvis-patch-log__frame--wide' : 'jarvis-patch-log__frame';

  const renderFigure = (image: PatchLogImagePublic, label?: string, aboveFold = false) => {
    const hasSize = image.width > 0 && image.height > 0;
    const alt = pickLocalized(image.altText, locale, '');
    const frameStyle = { aspectRatio: hasSize ? `${image.width} / ${image.height}` : undefined };
    const picture = (
      <img
        src={image.url}
        alt={alt}
        width={hasSize ? image.width : undefined}
        height={hasSize ? image.height : undefined}
        loading={aboveFold ? 'eager' : 'lazy'}
        decoding="async"
      />
    );

    return (
      <figure key={image.id} className="jarvis-patch-log__figure">
        {label ? <figcaption className="jarvis-patch-log__caption">{label}</figcaption> : null}
        {onZoomImage ? (
          // A real button, not an <img onClick>: Enter/Space, focus ring and an
          // accessible name that carries the locale's alt text come for free.
          <button
            type="button"
            className={`${frameClassName} jarvis-patch-log__frame--zoomable`}
            style={frameStyle}
            aria-label={
              alt ? `${alt} — ${resolvePatchLogCopy(locale, 'viewImage')}` : resolvePatchLogCopy(locale, 'viewImage')
            }
            onClick={(event) => {
              // Safari does not focus a button on click; without this the
              // viewer would have nothing to hand focus back to on close.
              event.currentTarget.focus();
              onZoomImage(image);
            }}
          >
            {picture}
          </button>
        ) : (
          <div className={frameClassName} style={frameStyle}>
            {picture}
          </div>
        )}
      </figure>
    );
  };

  return (
    <article className="jarvis-patch-log__entry" aria-labelledby={`patch-log-entry-${entry.id}`}>
      <div className="jarvis-patch-log__meta">
        <span className="jarvis-patch-log__badge" style={{ color: accent }}>
          <CategoryIcon size={14} stroke={2} style={{ flexShrink: 0 }} aria-hidden="true" />
          {resolvePatchLogCopy(locale, `category.${entry.category}` as const)}
        </span>

        {area ? (
          <span className="jarvis-patch-log__area">
            <IconMapPin size={11} stroke={2} style={{ flexShrink: 0 }} aria-hidden="true" />
            {/* Read aloud as "Page: Doc Pabean > Goods" instead of a bare fragment. */}
            <span className="jarvis-patch-log__sr-only">{`${resolvePatchLogCopy(locale, 'area')}: `}</span>
            <span>{area}</span>
          </span>
        ) : null}
      </div>

      <h3 id={`patch-log-entry-${entry.id}`} className="jarvis-patch-log__entry-title">
        {title}
      </h3>

      {pairs.length > 0 ? (
        <div className="jarvis-patch-log__images">
          {pairs.map((pair, index) => (
            <div key={pair.before?.id ?? pair.after?.id ?? index} className="jarvis-patch-log__compare">
              {pair.before
                ? renderFigure(pair.before, resolvePatchLogCopy(locale, 'before'), eager && index === 0)
                : null}
              {pair.after ? renderFigure(pair.after, resolvePatchLogCopy(locale, 'after'), eager && index === 0) : null}
            </div>
          ))}
        </div>
      ) : null}

      {singles.length > 0 ? (
        <div className="jarvis-patch-log__images">
          {singles.map((image, index) => renderFigure(image, undefined, eager && pairs.length === 0 && index === 0))}
        </div>
      ) : null}

      {points.length > 0 ? (
        <ul className="jarvis-patch-log__points">
          {points.map((point, index) => (
            // Points have no ids; position within the entry is their identity.
            // biome-ignore lint/suspicious/noArrayIndexKey: ordered, immutable list
            <li key={index}>{point}</li>
          ))}
        </ul>
      ) : null}
    </article>
  );
};

export default PatchLogEntryCard;
